import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  SxProps,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import React, { FC } from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { ExperienceModel } from '@/logic/services/experiences';
import FilterSkeleton from './movie-filters-skeleton';

import IndeterminateCheckBoxIcon from '@mui/icons-material/IndeterminateCheckBox';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useDialog } from '@/logic/state';
import ImageBoxFuture from '@/components/shared/image-box-future';

const EmptyExperienceImgCard = '/icons/cinema/emptyExperienceImgCard.svg';
const ExperienceIconBlack = '/icons/movie-details/experience-icon-black.svg';

interface ExperiencesFilterProps {
  typographySx: SxProps;
  experiences: ExperienceModel[];
  selectedExperiencesFilter: number[];
  isFetchingExperiences: boolean;
  returnSelectedExperiences: (experiencesIds: number[]) => void;
  showDisabledFilterMessage: () => void;
}
export const ExperiencesFilter: FC<ExperiencesFilterProps> = ({
  typographySx,
  experiences,
  selectedExperiencesFilter,
  isFetchingExperiences,
  returnSelectedExperiences,
  showDisabledFilterMessage,
}) => {
  const theme = useTheme();
  const intl = useIntl();
  const setDialog = useDialog(s => s.setDialog, shallow);

  const handleExperienceChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (checkIfExperienceDisabled(Number(event.target.value))) {
      showDisabledFilterMessage();
      return;
    }
    if (selectedExperiencesFilter.indexOf(Number(event.target.value)) < 0)
      returnSelectedExperiences([
        ...selectedExperiencesFilter,
        Number(event.target.value),
      ]);
    else {
      const index = selectedExperiencesFilter.indexOf(Number(event.target.value));
      const _selectedExperiencesFilter = [];
      Object.assign(_selectedExperiencesFilter, selectedExperiencesFilter);
      _selectedExperiencesFilter.splice(index, 1);
      returnSelectedExperiences([..._selectedExperiencesFilter]);
    }
  };

  const checkIfExperienceDisabled = (id: number): boolean => {
    return experiences.find(experience => experience.id === id)?.isDisabled;
  };
  const checkIfExperienceChecked = (id: number): boolean => {
    return !!selectedExperiencesFilter.find(cinemaId => cinemaId === id);
  };

  const FilterItem = ({ experience }: { experience: ExperienceModel }): JSX.Element => {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Checkbox
          value={experience.id}
          defaultChecked={checkIfExperienceChecked(experience.id)}
          indeterminateIcon={<IndeterminateCheckBoxIcon />}
          indeterminate={checkIfExperienceDisabled(experience.id)}
          sx={{
            '&.Mui-disabled': {
              color: theme.palette.grey[30],
            },
            '&.MuiCheckbox-indeterminate': {
              color: theme.palette.grey[30],
            },
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
          onChange={e => handleExperienceChange(e)}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <ImageBoxFuture
            img={experience.logo || EmptyExperienceImgCard}
            title={experience.logoAlt || experience?.name}
            layout='intrinsic'
            objectFit='contain'
            width='794px'
            height='397px'
            sx={{
              minWidth: {
                mobile: '92px',
                tablet: pxToRem(92),
              },
            }}
          />

          <Tooltip title={experience.description}>
            <Box
              sx={{
                paddingInlineStart: pxToRem(22),
                display: 'flex',
              }}
            >
              <Box
                sx={{
                  marginTop: pxToRem(22),
                  textAlign: 'start',
                }}
              >
                {/* <Typography
                  sx={{
                    fontSize: {
                      mobile: '18px',
                      tablet: pxToRem(18),
                    },
                    color: theme.palette.grey[80],
                    fontWeight: 700,
                    fontFamily: theme.palette.typography.font.bold,
                  }}
                >
                  {experience.name}
                </Typography> */}

                <>
                  <Typography
                    sx={{
                      fontSize: {
                        mobile: '14px',
                        tablet: pxToRem(16),
                      },
                      color: theme.palette.grey[50],
                      fontWeight: 400,
                      fontFamily: theme.palette.typography.font.regular,
                      WebkitLineClamp: '4',
                      lineHeight: '24px',
                      display: '-webkit-box',
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {experience.description}
                  </Typography>
                  <Button
                    sx={{
                      color: theme.palette.primary.main,
                      fontWeight: 700,
                      fontSize: pxToRem(16),
                      lineHeight: '24px',
                      cursor: 'pointer',
                    }}
                    onClick={() =>
                      setDialog({
                        wrapper: true,
                        isExperienceInfoDialog: true,
                        experienceInfoDialogPayload: experience,
                      })
                    }
                  >
                    <FormattedMessage id='movie_details.read.more' />
                  </Button>
                </>
              </Box>
            </Box>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  return (
    <Accordion
      defaultExpanded
      sx={{
        '&.Mui-expanded': {
          margin: 0,
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: theme.palette.grey[90] }} />}
        id='movie-details-experiences-filter'
        sx={{
          display: 'flex',
          alignItems: 'center',
          backgroundColor: 'transparent',
        }}
      >
        <Box
          component='div'
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginInlineEnd: '18px',
          }}
        >
          <ImageBoxFuture
            img={ExperienceIconBlack}
            title={intl.formatMessage({
              id: 'alt.experience_con',
            })}
            objectFit='contain'
            sx={{
              width: '20px',
              height: '18px',
            }}
          />
        </Box>
        <Typography sx={typographySx}>
          <FormattedMessage id='experience' />
        </Typography>
      </AccordionSummary>
      <Divider />
      <AccordionDetails sx={{ margin: pxToRem(24), marginTop: 0 }}>
        <FormGroup sx={{ mt: 3 }}>
          {!isFetchingExperiences &&
            experiences?.map(experience => (
              <FormControlLabel
                key={experience.id}
                control={<FilterItem experience={experience} />}
                label={''}
              />
            ))}
          {isFetchingExperiences &&
            [...Array(experiences?.length || 5)].map((i, _i: number) => (
              <FilterSkeleton key={_i} />
            ))}
        </FormGroup>
      </AccordionDetails>
    </Accordion>
  );
};

export default ExperiencesFilter;
